import { type PerceptionTarget, type Pose } from './types';

export interface PerceptionTuning {
  /** How fast the head turns toward a target, exponential damping per second. */
  headDamping: number;
  /** Snout leads the head; always LOWER damping than the head so it arrives after. */
  snoutDamping: number;
  earDamping: number;
  /** Clamps, radians. She has a neck, not a turret. */
  maxHeadYaw: number; maxHeadPitch: number;
  maxSnoutYaw: number; maxSnoutPitch: number;
  /** Per-kind multipliers on how much each channel responds. */
  kindGain: Record<PerceptionTarget['kind'], { head: number; snout: number; ear: number }>;
}

export const ROSALIE_PERCEPTION: PerceptionTuning = {
  // WHY: ears react first, head follows, and the snout settles last: 14 > 9 > 5.
  headDamping: 9,
  // WHY: this stays below head damping so the snout visibly trails attention.
  snoutDamping: 5,
  // WHY: this exceeds both other rates so ears twitch first toward a stimulus.
  earDamping: 14,
  // WHY: 50 degrees lets her inspect broadly while retaining a visible neck limit.
  maxHeadYaw: 0.87,
  // WHY: 28 degrees keeps vertical tracking from reading as a hinged turret.
  maxHeadPitch: 0.49,
  // WHY: the snout may lead a little farther sideways than the head.
  maxSnoutYaw: 1.05,
  // WHY: its shorter lever permits a slightly wider vertical investigating range.
  maxSnoutPitch: 0.61,
  kindGain: {
    // WHY: watched entities are head-led and use the full attention range.
    focus: { head: 1, snout: 0.7, ear: 0.55 },
    // WHY: a sound is localized chiefly by the ears before the head commits.
    sound: { head: 0.25, snout: 0.45, ear: 1 },
    // WHY: scent makes the snout investigate most while lowering the head.
    scent: { head: 0.7, snout: 1, ear: 0.65 },
    // WHY: usable things get a moderate, snout-led confirmation.
    interactable: { head: 0.6, snout: 0.9, ear: 0.45 },
  },
};

export class PerceptionSolver {
  readonly #tuning: PerceptionTuning;
  #target: PerceptionTarget | null = null;
  #headYaw = 0;
  #headPitch = 0;
  #snoutYaw = 0;
  #snoutPitch = 0;
  #earLFlick = 0;
  #earLSplay = 0;
  #earRFlick = 0;
  #earRSplay = 0;

  constructor(tuning: PerceptionTuning) {
    this.#tuning = tuning;
  }

  /** Point of attention, or null for "nothing in particular". */
  setTarget(target: PerceptionTarget | null): void {
    this.#target = target;
  }

  /**
   * Writes head/snout/ear channels into `pose`, ON TOP of whatever the clip put
   * there. Leaves every other channel untouched.
   * @param selfX/Y/Z  her position; @param facing her yaw, radians, 0 = +Z.
   */
  apply(dt: number, selfX: number, selfY: number, selfZ: number, facing: number, pose: Pose): void {
    const target = this.#target;
    let targetHeadYaw = 0;
    let targetHeadPitch = 0;
    let targetSnoutYaw = 0;
    let targetSnoutPitch = 0;
    let targetEarLFlick = 0;
    let targetEarLSplay = 0;
    let targetEarRFlick = 0;
    let targetEarRSplay = 0;

    if (target !== null) {
      const worldX = target.x - selfX;
      const worldY = target.y - selfY;
      const worldZ = target.z - selfZ;
      const cos = Math.cos(facing);
      const sin = Math.sin(facing);
      const localX = worldX * cos - worldZ * sin;
      const localZ = worldX * sin + worldZ * cos;
      const horizontal = Math.hypot(localX, localZ);
      const yaw = Math.atan2(localX, localZ);
      const pitch = -Math.atan2(worldY, horizontal);
      const gain = this.#tuning.kindGain[target.kind];
      const weight = clamp(target.weight, 0, 1);
      const scentDown = target.kind === 'scent' ? 0.18 * weight : 0;

      targetHeadYaw = clamp(yaw * gain.head * weight, -this.#tuning.maxHeadYaw, this.#tuning.maxHeadYaw);
      targetHeadPitch = clamp(
        pitch * gain.head * weight + scentDown,
        -this.#tuning.maxHeadPitch,
        this.#tuning.maxHeadPitch,
      );
      targetSnoutYaw = clamp(yaw * gain.snout * weight, -this.#tuning.maxSnoutYaw, this.#tuning.maxSnoutYaw);
      targetSnoutPitch = clamp(
        pitch * gain.snout * weight,
        -this.#tuning.maxSnoutPitch,
        this.#tuning.maxSnoutPitch,
      );

      const earResponse = gain.ear * weight;
      targetEarLFlick = clamp((pitch + Math.max(0, yaw) * 0.35) * earResponse, -0.35, 0.35);
      targetEarLSplay = clamp(yaw * earResponse, -0.45, 0.45);
      targetEarRFlick = clamp((pitch + Math.max(0, -yaw) * 0.35) * earResponse, -0.35, 0.35);
      targetEarRSplay = clamp(-yaw * earResponse, -0.45, 0.45);
    }

    const frameDt = Math.max(0, dt);
    const headStep = 1 - Math.exp(-this.#tuning.headDamping * frameDt);
    const snoutStep = 1 - Math.exp(-this.#tuning.snoutDamping * frameDt);
    const earStep = 1 - Math.exp(-this.#tuning.earDamping * frameDt);
    this.#headYaw += (targetHeadYaw - this.#headYaw) * headStep;
    this.#headPitch += (targetHeadPitch - this.#headPitch) * headStep;
    this.#snoutYaw += (targetSnoutYaw - this.#snoutYaw) * snoutStep;
    this.#snoutPitch += (targetSnoutPitch - this.#snoutPitch) * snoutStep;
    this.#earLFlick += (targetEarLFlick - this.#earLFlick) * earStep;
    this.#earLSplay += (targetEarLSplay - this.#earLSplay) * earStep;
    this.#earRFlick += (targetEarRFlick - this.#earRFlick) * earStep;
    this.#earRSplay += (targetEarRSplay - this.#earRSplay) * earStep;

    pose.headYaw += this.#headYaw;
    pose.headPitch += this.#headPitch;
    pose.snoutYaw += this.#snoutYaw;
    pose.snoutPitch += this.#snoutPitch;
    pose.earLFlick += this.#earLFlick;
    pose.earLSplay += this.#earLSplay;
    pose.earRFlick += this.#earRFlick;
    pose.earRSplay += this.#earRSplay;
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
